/* global debounce */

if (!customElements.get("hightlight-tab")) {
  class HightlightTab extends HTMLElement {
    constructor() {
      super();
      if (window.initLazyScript) {
        window.initLazyScript(this, this.init.bind(this));
      }
    }

    init() {
      // 初始化 sticky top：header 高度 - 1px（避免边线重叠）
      this.initStickyTop();

      this.tabsWrapper = this.querySelector(".hightlight-tabs");
      // 仅针对"锚点类型 tab"（非 a 标签）：有 data-hightlight 的元素
      this.tabs = this.querySelectorAll(".hightlight-tab[data-hightlight]");
      this.sections = [];
      this.isScrollingByClick = false;
      this.scrollTimeout = null;
      this.adjustTimeout = null;
      this._activeSectionId = "";
      this._observer = null;
      this._onScroll = null;
      this._onHashChange = null;
      // 用于在页面存在重复 data-hightlight-id 时，优先匹配本组件"下面"的 section
      this._selfPageTop = this.getBoundingClientRect().top + window.pageYOffset;

      this.tabs.forEach((tab) => {
        const id = tab.dataset.hightlight;
        const candidates = Array.from(
          document.querySelectorAll(`[data-hightlight-id="${id}"]`)
        ).filter(Boolean);

        // 如果同一个 id 有多个，优先选取出现在本组件下方的那个（避免命中 header/announcement 等）
        const section =
          candidates.find((el) => el.offsetTop >= this._selfPageTop - 10) ||
          candidates[0];
        this.sections.push(section);
      });

      this.initScroll();
      this.handleTabClick();
      this.initMouseDrag();
      this.initGradientMask();
      this.bindStickyTopResize();
      // StoreHeader / 字体加载后布局才稳定；与 theme 的 --content-start 写入时机对齐
      requestAnimationFrame(() => {
        requestAnimationFrame(() => this.initStickyTop());
      });
      setTimeout(() => this.initStickyTop(), 260);

      // 检查URL中的hash，如果有匹配的tab则触发点击
      this.handleInitialHash();
      
      // 监听hash变化事件，处理同一页面内的hash跳转
      this.handleHashChange();
    }

    initStickyTop() {
      const top = Math.max(0, this.getHeaderHeight() - 1);
      // hightlight-tab-section 是 section 的 class（在 schema 里配置）
      const sectionEl = this.closest(".hightlight-tab-section");
      (sectionEl || this).style.setProperty("--hightlight-tab-top", `${top}px`);
    }

    /**
     * 计算 sticky 偏移用的「可见导航高度」。
     * 默认只取导航本体（store-header/header），避免把公告栏等一起算成 98px。
     * 仅当公告栏本身是 fixed/sticky 且当前占据顶部时，才叠加其高度。
     */
    getHeaderHeight() {
      const root = document.documentElement;
      const parsePx = (val) => {
        const n = parseFloat(String(val || "").trim());
        return Number.isNaN(n) ? 0 : n;
      };
      let navHeight = 0;
      const storeHeader = document.querySelector("store-header.header");
      if (storeHeader) {
        navHeight =
          storeHeader.offsetHeight || storeHeader.getBoundingClientRect().height || 0;
      }
      if (!navHeight) {
        const headerSection = document.getElementById("shopify-section-header");
        if (headerSection) {
          navHeight =
            headerSection.offsetHeight ||
            headerSection.getBoundingClientRect().height ||
            0;
        }
      }
      if (!navHeight) {
        const headerVar = parsePx(
          getComputedStyle(root).getPropertyValue("--header-height")
        );
        if (headerVar > 0) {
          navHeight = headerVar;
        }
      }

      let announcementHeight = 0;
      const announcementSection = document.getElementById(
        "shopify-section-announcement"
      );
      if (announcementSection) {
        const style = getComputedStyle(announcementSection);
        const isStickyLike =
          style.position === "sticky" || style.position === "fixed";
        const rect = announcementSection.getBoundingClientRect();
        const isOccupyingTop = rect.bottom > 0 && rect.top <= 0;
        if (isStickyLike && isOccupyingTop) {
          announcementHeight =
            announcementSection.offsetHeight || rect.height || 0;
        }
      }

      const total = navHeight + announcementHeight;
      if (total > 0) {
        return Math.round(total);
      }

      const main = document.getElementById("main-content");
      if (main && window.scrollY < 2) {
        const top = main.getBoundingClientRect().top;
        if (top > 0) {
          return Math.round(top);
        }
      }

      const header =
        document.querySelector("header.header") ||
        document.querySelector(".header") ||
        document.querySelector("#shopify-section-header");
      if (header) {
        const rectH = header.getBoundingClientRect().height || 0;
        if (rectH > 0) {
          return Math.round(rectH);
        }
      }

      return 0;
    }

    /** iOS Safari 地址栏显隐时 window.resize 不一定触发，需监听 visualViewport */
    bindStickyTopResize() {
      if (this._stickyTopResizeBound) {
        return;
      }
      this._stickyTopResizeBound = true;

      const schedule = () => {
        clearTimeout(this._stickyTopResizeTimer);
        this._stickyTopResizeTimer = setTimeout(() => {
          this.initStickyTop();
        }, 150);
      };

      this._onStickyTopResize = schedule;
      window.addEventListener("resize", this._onStickyTopResize);
      if (window.visualViewport) {
        window.visualViewport.addEventListener(
          "resize",
          this._onStickyTopResize
        );
      }
      window.addEventListener("on:debounced-resize", this._onStickyTopResize);
    }

    initScroll() {
      // 优先使用 IntersectionObserver：用视口中心线命中 section（更稳定）
      if ("IntersectionObserver" in window) {
        // rootMargin: 上下各缩进 50%，只留下中间一条“中心带”
        // section 进入中心带时触发回调，符合“视口中心进入某 Section”
        const observer = new IntersectionObserver(
          (entries) => {
            if (this.isScrollingByClick) return;

            // 可能同时有多个 entry，取 intersection 最大的那个更稳
            const visible = entries
              .filter((e) => e.isIntersecting && e.target?.dataset?.hightlightId)
              .sort((a, b) => (b.intersectionRatio || 0) - (a.intersectionRatio || 0));

            // 没有任何 section 命中“中心带”时，需要解除高亮（否则会卡在第一个 tab）
            if (!visible.length) {
              if (this._activeSectionId) {
                this._activeSectionId = "";
                this.clearActiveTab();
              }
              return;
            }

            const currentSectionId = visible[0].target.dataset.hightlightId;
            if (currentSectionId && currentSectionId !== this._activeSectionId) {
              this._activeSectionId = currentSectionId;
              this.setActiveTab(currentSectionId);
            }
          },
          {
            root: null,
            rootMargin: "-50% 0px -50% 0px",
            threshold: [0, 0.01, 0.1, 0.25, 0.5, 0.75, 1],
          }
        );

        this.sections.forEach((section) => {
          if (section) observer.observe(section);
        });
        this._observer = observer;
        return;
      }

      // fallback：scroll + 视口中心点
      const onScroll = () => {
        if (this.isScrollingByClick) return;
        const centerY = window.pageYOffset + window.innerHeight / 2;

        let currentSectionId = "";
        for (const section of this.sections) {
          if (!section) continue;
          const top = section.offsetTop;
          const bottom = top + section.offsetHeight;
          if (centerY >= top && centerY <= bottom) {
            currentSectionId = section.dataset.hightlightId;
            break;
          }
        }

        // 没命中任何 section：解除高亮
        if (!currentSectionId) {
          if (this._activeSectionId) {
            this._activeSectionId = "";
            this.clearActiveTab();
          }
          return;
        }

        if (currentSectionId !== this._activeSectionId) {
          this._activeSectionId = currentSectionId;
          this.setActiveTab(currentSectionId);
        }
      };

      // 用 rAF 降噪
      let ticking = false;
      this._onScroll = () => {
        if (ticking) return;
        ticking = true;
        window.requestAnimationFrame(() => {
          ticking = false;
          onScroll();
        });
      };

      window.addEventListener("scroll", this._onScroll, { passive: true });
      // 初始化时也跑一次，避免刷新后高亮不对
      onScroll();
    }

    handleTabClick() {
      const self = this;

      // 点击 tab 滚动到对应区域
      this.tabs.forEach((tab) => {
        tab.addEventListener("click", () => {
          const targetId = tab.dataset.hightlight;
          const targetSection = document.querySelector(
            `[data-hightlight-id='${targetId}']`
          );

          if (targetSection) {
            // 设置点击状态，暂时禁用 scroll 激活
            self.isScrollingByClick = true;

            // 手动激活 tab
            self.tabs.forEach((t) => t.classList.remove("is-active"));
            tab.classList.add("is-active");
            self._activeSectionId = targetId;
            this.scrollTabToCenter(tab);
            
            // 清除之前的定时器
            clearTimeout(self.scrollTimeout);
            clearTimeout(self.adjustTimeout);
            
            // 获取当前滚动位置，判断方向
            const currentScrollY = window.pageYOffset || document.documentElement.scrollTop;
            
            // 使用 offsetTop 计算元素在文档中的绝对位置
            let absoluteTop = 0;
            let el = targetSection;
            
            // 累加所有 offsetTop 得到绝对位置
            while (el && el !== document.body) {
              absoluteTop += el.offsetTop;
              el = el.offsetParent;
            }
            
            // 判断是否从下往上滚动
            const isScrollingUp = absoluteTop < currentScrollY;
            
            // 使用 requestAnimationFrame 确保在布局完成后再计算偏移量
            requestAnimationFrame(() => {
              // 获取偏移量（header + tab + gap）
              const offset = this.getScrollOffset();
              
              // 最终目标位置
              const targetPosition = absoluteTop - offset;
              
              // 执行滚动
              window.scrollTo({
                top: targetPosition,
                behavior: "smooth",
              });
            });

            // 微调仅执行一次，避免 macOS 惯性滚动期间反复二次动画导致“横跳”
            self.adjustTimeout = setTimeout(() => {
              requestAnimationFrame(() => {
                const rect = targetSection.getBoundingClientRect();
                const currentOffset = this.getScrollOffset();
                const diff = rect.top - currentOffset;
                
                // 如果有明显偏差，做一次“无动画”修正，避免二次 smooth 与原生惯性叠加
                if (Math.abs(diff) > 4) {
                  const currentY = window.pageYOffset || document.documentElement.scrollTop;
                  const targetY = currentY + diff;
                  window.scrollTo({
                    top: targetY,
                    behavior: "auto",
                  });
                }
                
                self.isScrollingByClick = false;
              });
            }, isScrollingUp ? 420 : 360);
          }
        });
      });
    }

    /**
     * 计算点击 tab 滚动时的顶部偏移，避免 tab/header 覆盖 section 标题
     */
    getScrollOffset() {
      const gap = 24; // 额外留白，确保标题不贴边

      const headerHeight = this.getHeaderHeight();

      // 2) 获取整个 hightlight-tab-section 的高度（包括所有内容）
      // 因为 section 是 sticky 的，需要获取整个 section 的高度
      const sectionEl = this.closest(".hightlight-tab-section");
      let tabHeight = 0;
      
      if (sectionEl) {
        // 获取整个 section 的高度
        const sectionRect = sectionEl.getBoundingClientRect();
        tabHeight = sectionRect.height || 0;
      } else {
        // 如果没有找到 section，使用组件本身的高度
        const tabRect = this.getBoundingClientRect();
        tabHeight = tabRect.height || 0;
      }

      // 3) 增加额外的安全边距，确保内容完全可见
      const extraMargin = 32; // 增加更多边距，确保标题完全可见
      
      return headerHeight + tabHeight + gap + extraMargin;
    }

    setActiveTab(currentSectionId) {
      const self = this;
      this.tabs.forEach((tab) => {
        tab.classList.remove("is-active");
        if (tab.dataset.hightlight === currentSectionId) {
          tab.classList.add("is-active");
          self.scrollTabToCenter(tab);
        }
      });
    }

    clearActiveTab() {
      this.tabs.forEach((tab) => tab.classList.remove("is-active"));
    }

    scrollTabToCenter(tab) {
      const tabRect = tab.getBoundingClientRect();
      const wrapperRect = this.tabsWrapper.getBoundingClientRect();
      const wrapperCenter = wrapperRect.width / 2;
      const tabCenter = tabRect.left - wrapperRect.left + tabRect.width / 2;
      const scrollLeft =
        this.tabsWrapper.scrollLeft + tabCenter - wrapperCenter;

      this.tabsWrapper.scrollTo({
        left: scrollLeft,
        behavior: "smooth",
      });
    }

    /**
     * 自定义平滑滚动动画（用于微调，避免 behavior: "smooth" 的固定时长）
     * @param {number} targetPosition - 目标滚动位置
     * @param {number} duration - 动画时长（毫秒）
     */
    smoothScrollTo(targetPosition, duration = 300) {
      const startPosition = window.pageYOffset || document.documentElement.scrollTop;
      const distance = targetPosition - startPosition;
      const startTime = performance.now();

      // easeOutCubic 缓动函数：开始快→结束慢（模拟惯性衰减）
      const easeOutCubic = (t) => {
        return 1 - Math.pow(1 - t, 3);
      };

      const animation = (currentTime) => {
        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / duration, 1);
        const easeProgress = easeOutCubic(progress);
        
        window.scrollTo(0, startPosition + distance * easeProgress);

        if (progress < 1) {
          requestAnimationFrame(animation);
        }
      };

      requestAnimationFrame(animation);
    }

    /**
     * PC 端鼠标拖动滑动
     */
    initMouseDrag() {
      if (!this.tabsWrapper) return;

      let isDown = false;
      let startX;
      let scrollLeft;

      this.tabsWrapper.addEventListener('mousedown', (e) => {
        isDown = true;
        startX = e.pageX - this.tabsWrapper.offsetLeft;
        scrollLeft = this.tabsWrapper.scrollLeft;
        this.tabsWrapper.style.cursor = 'grabbing';
      });

      this.tabsWrapper.addEventListener('mouseleave', () => {
        isDown = false;
        this.tabsWrapper.style.cursor = 'grab';
      });

      this.tabsWrapper.addEventListener('mouseup', () => {
        isDown = false;
        this.tabsWrapper.style.cursor = 'grab';
      });

      this.tabsWrapper.addEventListener('mousemove', (e) => {
        if (!isDown) return;
        e.preventDefault();
        const x = e.pageX - this.tabsWrapper.offsetLeft;
        const walk = (x - startX) * 2;
        this.tabsWrapper.scrollLeft = scrollLeft - walk;
      });
    }

    /**
     * 初始化文字渐变透明效果
     */
    initGradientMask() {
      const container = this.querySelector('.hightlight-tab-container');
      const tabsWrapper = this.tabsWrapper;
      
      if (!container || !tabsWrapper) return;

      const updateMaskVisibility = () => {
        const scrollLeft = tabsWrapper.scrollLeft;
        const scrollWidth = tabsWrapper.scrollWidth;
        const clientWidth = tabsWrapper.clientWidth;
        const maxScroll = scrollWidth - clientWidth;


        // 判断是否可以向左滚动（不在最左边）
        if (scrollLeft > 5) {
          container.classList.add('can-scroll-left');
        } else {
          container.classList.remove('can-scroll-left');
        }

        // 判断是否可以向右滚动（不在最右边）
        if (scrollLeft < maxScroll - 5) {
          container.classList.add('can-scroll-right');
        } else {
          container.classList.remove('can-scroll-right');
        }
      };

      // 初始化显示状态
      setTimeout(updateMaskVisibility, 100);

      // 监听滚动事件
      tabsWrapper.addEventListener('scroll', updateMaskVisibility);

      // 监听窗口大小变化
      window.addEventListener('resize', updateMaskVisibility);
    }

    /**
     * 根据hash找到对应的tab
     * @param {string} hash - URL中的hash值（不带#）
     * @returns {HTMLElement|null} 匹配的tab元素，如果没找到返回null
     */
    findTabByHash(hash) {
      if (!hash) return null;

      // 方法1: 直接匹配tab的data-hightlight属性（向后兼容）
      let matchingTab = Array.from(this.tabs).find(
        (tab) => tab.dataset.hightlight === hash
      );

      if (matchingTab) return matchingTab;

      // 方法2: 通过section的data-hightlight-id匹配
      // 查找data-hightlight-id等于hash的section
      const matchingSection = document.querySelector(
        `[data-hightlight-id="${hash}"]`
      );

      if (matchingSection) {
        // 通过section的data-hightlight-id找到对应的tab
        matchingTab = Array.from(this.tabs).find(
          (tab) => tab.dataset.hightlight === hash
        );
        if (matchingTab) return matchingTab;
      }

      // 方法3: 如果hash是Shopify section ID格式，尝试查找对应的section
      // Shopify section ID格式通常是: shopify-section-xxx 或包含 section id
      // 例如: shopify-section-template--17670703120499__pdp_media_with_text_pFrN6r
      if (hash.includes('shopify-section') || hash.includes('section')) {
        // 尝试查找id等于hash的元素（可能是section本身）
        const elementById = document.getElementById(hash);
        
        if (elementById) {
          // 查找该元素或其父元素是否有data-hightlight-id属性
          // 先检查元素本身
          let sectionWithId = elementById.hasAttribute('data-hightlight-id') 
            ? elementById 
            : null;
          
          // 如果元素本身没有，向上查找父元素
          if (!sectionWithId) {
            sectionWithId = elementById.closest('[data-hightlight-id]');
          }
          
          // 如果还是没找到，尝试查找该元素内部的data-hightlight-id元素
          if (!sectionWithId) {
            sectionWithId = elementById.querySelector('[data-hightlight-id]');
          }
          
          if (sectionWithId) {
            const sectionId = sectionWithId.getAttribute('data-hightlight-id');
            // 通过section的data-hightlight-id找到对应的tab
            matchingTab = Array.from(this.tabs).find(
              (tab) => tab.dataset.hightlight === sectionId
            );
            if (matchingTab) return matchingTab;
          } else {
            // 如果找不到data-hightlight-id，但找到了元素本身
            // 尝试直接通过元素查找对应的section（可能元素本身就是section）
            // 查找所有sections，看哪个包含这个元素
            for (const section of this.sections) {
              if (section && (section.contains(elementById) || section === elementById)) {
                const sectionId = section.getAttribute('data-hightlight-id');
                if (sectionId) {
                  matchingTab = Array.from(this.tabs).find(
                    (tab) => tab.dataset.hightlight === sectionId
                  );
                  if (matchingTab) return matchingTab;
                }
              }
            }
          }
        }
      }

      return null;
    }

    /**
     * 处理URL中的hash锚点，如果匹配到对应的tab则触发点击事件
     */
    handleInitialHash() {
      // 获取URL中的hash（去掉#号）
      const hash = window.location.hash.slice(1);
      if (!hash) return;

      // 使用重试机制，确保DOM完全加载后能找到对应的tab
      let attempts = 0;
      const maxAttempts = 20; // 最多尝试20次，每次100ms，总共2秒
      
      const tryFindAndClick = () => {
        attempts++;
        
        // 查找匹配的tab
        const matchingTab = this.findTabByHash(hash);

        if (matchingTab) {
          // 延迟执行，确保页面完全加载和布局完成
          // 使用更长的延迟确保在浏览器默认的hash跳转之后执行
          setTimeout(() => {
            // 使用 requestAnimationFrame 确保在布局完成后执行
            requestAnimationFrame(() => {
              requestAnimationFrame(() => {
                // 触发点击事件，这会执行 handleTabClick 中的逻辑
                matchingTab.click();
              });
            });
          }, 300);
        } else if (attempts < maxAttempts) {
          // 如果没找到，继续重试
          setTimeout(tryFindAndClick, 100);
        }
      };
      
      // 立即尝试一次
      setTimeout(tryFindAndClick, 100);
      
      // 如果页面还没加载完成，等待load事件后再尝试
      if (document.readyState !== 'complete') {
        window.addEventListener('load', () => {
          setTimeout(tryFindAndClick, 300);
        }, { once: true });
      }
    }

    /**
     * 监听hash变化事件，处理同一页面内的hash跳转
     */
    handleHashChange() {
      const self = this;
      
      // 监听hash变化
      this._onHashChange = () => {
        const hash = window.location.hash.slice(1);
        if (!hash) return;

        // 查找匹配的tab
        const matchingTab = this.findTabByHash(hash);

        if (matchingTab) {
          // 延迟执行，确保浏览器默认的hash跳转完成
          setTimeout(() => {
            // 触发点击事件，这会执行 handleTabClick 中的逻辑
            matchingTab.click();
          }, 50);
        }
      };
      
      window.addEventListener('hashchange', this._onHashChange);
    }

    disconnectedCallback() {
      if (this._observer) {
        this._observer.disconnect();
        this._observer = null;
      }
      if (this._onScroll) {
        window.removeEventListener("scroll", this._onScroll);
        this._onScroll = null;
      }
      if (this._onHashChange) {
        window.removeEventListener("hashchange", this._onHashChange);
        this._onHashChange = null;
      }
      if (this._onStickyTopResize) {
        window.removeEventListener("resize", this._onStickyTopResize);
        window.removeEventListener(
          "on:debounced-resize",
          this._onStickyTopResize
        );
        if (window.visualViewport) {
          window.visualViewport.removeEventListener(
            "resize",
            this._onStickyTopResize
          );
        }
        this._onStickyTopResize = null;
      }
      clearTimeout(this._stickyTopResizeTimer);
    }
  }

  customElements.define("hightlight-tab", HightlightTab);
}
